import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { FacilitySwitcherItem } from '../../api/superAdmin/facilitySwitcher/useFacilitySwitcherSearch';
import FacilitySearchModal, { FacilityIdentity } from './FacilitySearchModal';
import { useSwitchToFacility } from './useSwitchToFacility';

interface StaffHotelSearchModalProps {
    open: boolean;
    setOpen: (value: boolean) => void;
}

const crmLink = (id: number) => `/super-admin/crm/facilities/${id}`;
const settingsLink = (id: number) => `/super-admin/facilities/update/${id}`;

/**
 * Recherche d'hôtel du back-office (super admin), ouverte par le menu ou Cmd+K.
 *
 * Remplace le sélecteur d'établissement en mode back-office : on n'y change
 * pas d'hôtel pour rester sur place, on ouvre la fiche CRM, les paramètres,
 * ou l'application telle que l'hôtel la voit.
 */
export default function StaffHotelSearchModal({ open, setOpen }: StaffHotelSearchModalProps) {
    const { t } = useTranslation('navigation');
    const navigate = useNavigate();
    const { switchTo, pendingId } = useSwitchToFacility();

    const viewAsClient = async (facilityId: number, close: () => void) => {
        if (await switchTo(facilityId)) {
            close();
            navigate('/dashboard');
        }
    };

    return (
        <FacilitySearchModal
            open={open}
            setOpen={setOpen}
            title={t('staff_shell.search_title')}
            placeholder={t('staff_shell.search_placeholder')}
            size="lg"
            listboxId="staff-hotel-search-results"
            keyboardHint={t('staff_shell.keyboard_hint')}
            onEnter={(facility, close) => {
                close();
                navigate(crmLink(facility.id));
            }}
            renderRow={(facility, close) => (
                <HotelRow
                    facility={facility}
                    isPending={pendingId === facility.id}
                    disabled={pendingId !== null}
                    onNavigate={close}
                    onViewAsClient={(id) => viewAsClient(id, close)}
                />
            )}
        />
    );
}

interface HotelRowProps {
    facility: FacilitySwitcherItem;
    isPending: boolean;
    disabled: boolean;
    onNavigate: () => void;
    onViewAsClient: (facilityId: number) => void;
}

function HotelRow({ facility, isPending, disabled, onNavigate, onViewAsClient }: HotelRowProps) {
    const { t } = useTranslation('navigation');
    const action = 'rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-main-color';

    return (
        <div className="flex flex-col gap-2 px-3 py-2.5 sm:flex-row sm:items-center">
            <FacilityIdentity facility={facility} />
            <div className="flex flex-shrink-0 gap-1.5">
                <Link to={crmLink(facility.id)} onClick={onNavigate} className={`${action} bg-main-color text-white hover:bg-main-color-dark`}>
                    {t('staff_shell.open_crm')}
                </Link>
                <Link to={settingsLink(facility.id)} onClick={onNavigate} className={`${action} bg-gray-100 text-gray-700 hover:bg-gray-200`}>
                    {t('staff_shell.open_settings')}
                </Link>
                <button
                    type="button"
                    disabled={disabled}
                    onClick={() => onViewAsClient(facility.id)}
                    className={`${action} bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-60`}
                >
                    {isPending ? '…' : t('staff_shell.view_as_client')}
                </button>
            </div>
        </div>
    );
}
